import { api, isHtmlInterstitialError, isClerkReauthPausedError } from './client'
import { useSessionStore } from '@/stores/sessionStore'
import { isOverrideErrorCode } from '@/lib/discountLimit'

/**
 * Build a register-scoped v2 URL: `/ht/api/v2/stores/{sid}/registers/{rid}{path}`.
 * Reads store + register from the session store. Throws if neither is selected,
 * since every register-scoped endpoint requires both.
 */
export function v2(path: string): string {
  const { selectedStore, selectedRegister } = useSessionStore.getState()
  if (!selectedStore?.id || !selectedRegister?.id) {
    throw new Error('v2: store and register must be selected before calling register-scoped endpoints')
  }
  return `/ht/api/v2/stores/${selectedStore.id}/registers/${selectedRegister.id}${path}`
}

/**
 * Build an unscoped v2 URL: `/ht/api/v2{path}`.
 * Use for `/auth/*`, `/objects/*`, `/sys_config`, `/gift_cards/*`,
 * `/customers/insights`, `/customers/activity`, `/customers/{id}/verify-age`.
 */
export function v2Unscoped(path: string): string {
  return `/ht/api/v2${path}`
}

/**
 * Generate a fresh idempotency key for payment-creating POSTs (`/payments`,
 * `/pay_out`, `/receive-on-account`). Generate ONE key per logical attempt
 * and reuse it across network-level retries of the exact same payload.
 */
export function idempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/** v2 error-envelope body (HTTP 4xx/5xx, occasionally 2xx). */
export interface V2ErrorBody {
  success: false
  error: string
  code: string | null
  message?: string
  sanity_issues?: string[]
  // 409 ambiguous_barcode on /products/by-barcode -- full Product objects
  // for each catalog candidate sharing the scanned code.
  candidates?: unknown[]
  // Structured context the BE attaches via V2OperationError(**extra).
  // Serialized by envelope_error in hypnotoad/handlers/api/v2/_base.py
  // when non-empty. Caller-specific shape per error `code` -- e.g.
  // 409 register_busy carries {busy_session_id: <uuid>} so the FE can
  // offer cancel-and-retry instead of bouncing the cashier for the
  // 6-minute reaper window.
  extra?: Record<string, unknown>
}

/** v2 success-envelope body (used by /payments, /pay_out, /receive-on-account, /copy-with-refund, /transactions/{id}/payment-type). */
export interface V2Envelope<T> {
  success: true
  status?: string
  data: T
}

export class V2ApiError extends Error {
  status: number
  code: string | null
  detail?: string
  sanityIssues?: string[]
  candidates?: unknown[]
  // Structured context attached to the BE error (V2ErrorBody.extra).
  // Caller-specific shape per error `code`. Consumers should narrow
  // before reading -- e.g. `extra?.busy_session_id` for register_busy.
  extra?: Record<string, unknown>

  constructor(
    status: number,
    message: string,
    code: string | null,
    detail?: string,
    sanityIssues?: string[],
    candidates?: unknown[],
    extra?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'V2ApiError'
    this.status = status
    this.code = code
    this.detail = detail
    this.sanityIssues = sanityIssues
    this.candidates = candidates
    this.extra = extra
  }

  static fromBody(status: number, body: V2ErrorBody): V2ApiError {
    return new V2ApiError(
      status,
      body.error,
      body.code,
      body.message,
      body.sanity_issues,
      body.candidates,
      body.extra,
    )
  }
}

/**
 * Cashier-facing text for a failed v2 call.
 *
 * Lives next to `V2ApiError.fromBody` because the field mapping is easy
 * to get backwards, and getting it backwards silently downgrades every
 * message to the generic fallback:
 *
 *   envelope `error`   -> `err.message`  (the actionable text; the BE's
 *                         envelope_error always populates this one)
 *   envelope `message` -> `err.detail`   (richer, but only a handful of
 *                         endpoints set it)
 *
 * `err.response.data.error` yields undefined no matter what: v2api is
 * fetch-based and throws V2ApiError, never an axios error. Several call
 * sites still read that shape and survive only on their `|| err.message`
 * tail.
 */
export function v2ErrorText(err: unknown, fallback: string): string {
  if (err instanceof V2ApiError) return err.detail || err.message || fallback
  if (err instanceof Error) return err.message || fallback
  return fallback
}

/** Error `code` from a v2 failure, for branching. Null when absent. */
export function v2ErrorCode(err: unknown): string | null {
  return err instanceof V2ApiError ? err.code : null
}

function isV2ErrorBody(body: unknown): body is V2ErrorBody {
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { success?: unknown }).success === false &&
    typeof (body as { error?: unknown }).error === 'string'
  )
}

/**
 * Throw V2ApiError when the body is a v2 error envelope. Some v2 endpoints
 * return the envelope with HTTP 200, so we must inspect the body shape rather
 * than relying on status codes alone.
 */
export function unwrapV2<T>(body: T | V2ErrorBody, status = 200): T {
  if (isV2ErrorBody(body)) {
    throw V2ApiError.fromBody(status, body)
  }
  return body as T
}

/**
 * HTTP status of a failed v2 call, whatever shape it arrived in: a
 * V2ApiError (envelope body) or the raw transport error rethrown by
 * `rethrowV2` for non-envelope bodies (tornado's HTML 404/403 pages).
 * `null` when there was no response at all (offline, aborted).
 */
export function v2ErrorStatus(err: unknown): number | null {
  if (err instanceof V2ApiError) return err.status
  const status = (err as { response?: { status?: unknown } } | null)?.response?.status
  return typeof status === 'number' ? status : null
}

function rethrowV2(err: unknown): never {
  const e = err as { response?: { status?: number; data?: unknown } }
  const status = e?.response?.status ?? 500
  const data = e?.response?.data
  if (isV2ErrorBody(data)) {
    throw V2ApiError.fromBody(status, data)
  }
  throw err
}

/**
 * Recovery class for a failed v2 read (today: /cart/preview). The distinction
 * is operational, not cosmetic — it decides whether retrying the SAME request
 * can ever succeed, and what the cashier should be told:
 *
 *   - offline   : the device has no network (`navigator.onLine === false`).
 *                 Recovers on its own when connectivity returns.
 *   - transport : reached *something* but got an HTML login/proxy interstitial
 *                 (see isHtmlInterstitialError). Clears when the portal/LB does
 *                 — retryable, and NOT a backend rejection.
 *   - auth      : 401/403. The retry loop re-drives the token refresh on every
 *                 attempt, so a transient refresh failure heals untouched.
 *   - rejected  : a deterministic 4xx for THIS basket (bad line/lot/qty, etc.).
 *                 The BE will keep rejecting the identical payload, so retrying
 *                 is futile — surface the reason so the cashier fixes the cart.
 *   - transient : 408/429/5xx, a network error with no response, or an aborted/
 *                 hung request. Retrying the same payload is the right recovery.
 */
export type PreviewErrorKind =
  | 'offline'
  | 'transport'
  | 'auth'
  | 'rejected'
  | 'transient'

export interface PreviewErrorInfo {
  kind: PreviewErrorKind
  /** HTTP status when known (V2ApiError); null for network/transport/offline. */
  status: number | null
  /** BE error `code` when known — e.g. 'price_drift', 'sanity_check_failed'. */
  code: string | null
  /** Human-readable BE detail when known, for the 'rejected' cashier message. */
  message: string | null
  /** Structured BE context (V2ApiError.extra), e.g. {limit, requested} on a
   *  409 discount_exceeds_limit. */
  extra?: Record<string, unknown> | null
}

export function classifyPreviewError(err: unknown): PreviewErrorInfo {
  // Order matters. A V2ApiError means an HTTP round-trip to the BE COMPLETED,
  // so it is authoritative about the failure — classify by status FIRST,
  // before consulting navigator.onLine (a weak heuristic that can lag or flip
  // a tick after the response lands). Checking online first would mislabel a
  // real backend rejection as 'offline' if the flag flipped, hiding the reason
  // and implying a retry would help when it won't.
  if (err instanceof V2ApiError) {
    const s = err.status
    let kind: PreviewErrorKind
    // The discount-limit gate's override_* refusals (409; 403 on older builds) are business
    // refusals of a manager PIN, not an auth failure: 'rejected', so the banner shows the
    // reason and re-prompts instead of "Refreshing your session".
    if (isOverrideErrorCode(err.code)) kind = 'rejected'
    else if (s === 401 || s === 403) kind = 'auth'
    else if (s === 408 || s === 429 || s >= 500) kind = 'transient'
    else if (s >= 400) kind = 'rejected'
    else kind = 'transient'
    return {
      kind,
      status: s,
      code: err.code,
      message: err.detail ?? err.message ?? null,
      ...(err.extra ? { extra: err.extra } : {}),
    }
  }
  // Reached *something* that answered with an HTML login/proxy page rather
  // than JSON (no usable status on the synthetic error — see client.ts).
  if (isHtmlInterstitialError(err)) {
    return { kind: 'transport', status: null, code: null, message: null }
  }
  // Synthetic local rejection from the clerk re-auth circuit breaker
  // (client.ts): the request never left the device because the PIN
  // overlay is up. Classify as 'auth' — re-authentication is literally
  // the pending recovery — so the bridge doesn't burn its transient
  // retry/reconnect UX on requests that are deliberately paused; normal
  // service resumes the moment the PIN lands and the breaker opens.
  if (isClerkReauthPausedError(err)) {
    return { kind: 'auth', status: null, code: 'clerk_reauth_pending', message: null }
  }
  // No HTTP response came back at all (network error / abort / unknown). This
  // is the ONLY case where navigator.onLine is meaningful: false => genuinely
  // offline; true => a transient network/transport blip the self-heal loop
  // should keep retrying.
  const online = typeof navigator === 'undefined' || navigator.onLine !== false
  return online
    ? { kind: 'transient', status: null, code: null, message: null }
    : { kind: 'offline', status: null, code: null, message: null }
}

/**
 * Thin wrappers around `api.*` that automatically unwrap the v2 envelope and
 * convert any `{success: false, ...}` body into a `V2ApiError`. Use these in
 * every v2 hook so individual hooks stay free of envelope plumbing.
 */
export const v2api = {
  get: async <T>(url: string, params?: Record<string, unknown>, opts?: { signal?: AbortSignal }): Promise<T> => {
    try {
      const body = await api.get<T | V2ErrorBody>(url, params, opts)
      return unwrapV2<T>(body)
    } catch (e) {
      rethrowV2(e)
    }
  },

  post: async <T>(url: string, data?: object, opts?: { signal?: AbortSignal }): Promise<T> => {
    try {
      const body = await api.post<T | V2ErrorBody>(url, data, opts)
      return unwrapV2<T>(body)
    } catch (e) {
      rethrowV2(e)
    }
  },

  put: async <T>(url: string, data?: object): Promise<T> => {
    try {
      const body = await api.put<T | V2ErrorBody>(url, data)
      return unwrapV2<T>(body)
    } catch (e) {
      rethrowV2(e)
    }
  },

  patch: async <T>(url: string, data?: object): Promise<T> => {
    try {
      const body = await api.patch<T | V2ErrorBody>(url, data)
      return unwrapV2<T>(body)
    } catch (e) {
      rethrowV2(e)
    }
  },

  delete: async <T>(url: string, data?: object): Promise<T> => {
    try {
      const body = await api.delete<T | V2ErrorBody>(url, data)
      return unwrapV2<T>(body)
    } catch (e) {
      rethrowV2(e)
    }
  },
}
